import {
  Button,
  DateInput,
  Modal,
  OutlinedSelect,
  OutlinedSelectOptionType,
  addToast,
  useMobile,
} from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import useUserState from '../../../../../../hooks/useUserState';
import { useValidations } from '../../../../../../hooks/useValidations';
import { PermissionName } from '../../../../../../types/Auth';
import { downloadFromBlob } from '../../../../../../utils/blob';
import { baseToSelectOption } from '../../../../../academicOffers/courses/utils';
import { getTeachersOccupationFormData } from '../../api';
import GeneratingFileModal from '../GeneratingFileModal';
import NoResultsModal from '../NoResultsModal';
import { createReportDownloadHandler } from './handlerCreator';

enum ModalType {
  NONE,
  FILTER_SELECTOR,
  GENERATING_FILE,
  NO_RESULTS,
}

interface ReportTeachersOccupationProps {
  downloadUrl: string;
}

const DEFAULT_VALUES = {
  initDate: null,
  endDate: null,
  schoolId: null,
  campusId: null,
};

interface FormValues {
  initDate: Date | null;
  endDate: Date | null;
  schoolId: OutlinedSelectOptionType | null;
  campusId: OutlinedSelectOptionType | null;
}

const HTTP_STATUS_NO_CONTENT = 204;

export default function ReportTeachersOccupation({
  downloadUrl,
}: ReportTeachersOccupationProps) {
  const isMobile = useMobile();
  const { t } = useTranslation();
  const { msgValidations } = useValidations();
  const { isAuthorizedTo } = useUserState();

  const [openedModal, setOpenedModal] = useState<ModalType>(ModalType.NONE);
  const [formLoading, setFormLoading] = useState(false);
  const [schools, setSchools] = useState<{ id: number; name: string }[]>([]);
  const [campuses, setCampuses] = useState<{ id: number; name: string }[]>([]);

  const texts = useMemo(() => {
    return {
      title: t('reports.teachersOccupation.title'),
    };
  }, [t]);

  const { control, watch, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
    shouldUnregister: true,
  });

  const { initDate } = watch();

  const getInputs = useCallback(async () => {
    setFormLoading(true);
    const { data, error } = await getTeachersOccupationFormData();
    if (error) {
      throw new Error(error.code);
    } else if (data) {
      setSchools(data.schools);
      setCampuses(data.campuses);
    }
    setFormLoading(false);
  }, [setSchools, setCampuses]);

  useEffect(() => {
    reset();
    if (openedModal === ModalType.FILTER_SELECTOR) {
      getInputs();
    }
  }, [getInputs, openedModal, reset]);

  const optionsSchools = useMemo<OutlinedSelectOptionType[]>(() => {
    return baseToSelectOption(schools);
  }, [schools]);

  const optionsCampuses = useMemo<OutlinedSelectOptionType[]>(() => {
    return baseToSelectOption(campuses);
  }, [campuses]);

  const handleReportDownload = createReportDownloadHandler({
    beforeDownload: () => setOpenedModal(ModalType.GENERATING_FILE),
    afterDownload: ({ status }) => {
      if (status === HTTP_STATUS_NO_CONTENT) {
        setOpenedModal(ModalType.NO_RESULTS);
      } else {
        setOpenedModal(ModalType.NONE);
      }
    },
    onSuccess: (data: Blob) => {
      if (data.size > 0) {
        const blob = new Blob([data], { type: 'vnd.ms-excel' });
        downloadFromBlob(blob, `ocupacion-docentes.xlsx`);
      }
    },
    onError: () => {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`reports.errors.errorGeneratingFile`),
      });
    },
  });

  const handleParamsSelection = (values: FormValues) => {
    handleReportDownload(downloadUrl, {
      initDate: values.initDate,
      endDate: values.endDate,
      schoolId: values.schoolId?.value,
      campusId: values.campusId?.value,
    });
  };

  return (
    <>
      <NoResultsModal
        isOpen={openedModal === ModalType.NO_RESULTS}
        onConfirm={() => setOpenedModal(ModalType.NONE)}
        texts={{
          title: t('reports.teachersOccupation.noResults.title'),
          description: t('reports.teachersOccupation.noResults.description'),
        }}
      />
      <GeneratingFileModal isOpen={openedModal === ModalType.GENERATING_FILE} />
      {openedModal === ModalType.FILTER_SELECTOR && (
        <Modal isOpen toggle={() => null}>
          <Form
            onSubmit={handleSubmit((values) => {
              return handleParamsSelection(values);
            })}
          >
            <h1 className="text-dark text-center fw-600 fs-20">
              {texts.title}
            </h1>

            <Row className="pt-4">
              <Col sm={12} md={6}>
                <DateInput
                  name="initDate"
                  label={t(`common.forms.dateFrom`)}
                  control={control}
                  rules={{
                    required: msgValidations.required,
                  }}
                />
              </Col>
              <Col sm={12} md={6}>
                <DateInput
                  name="endDate"
                  label={t(`common.forms.dateTo`)}
                  control={control}
                  minDate={initDate ? dayjs(initDate).toDate() : undefined}
                  rules={{
                    required: msgValidations.required,
                  }}
                />
              </Col>
              <Col xs={12} className="mt-2">
                <OutlinedSelect
                  name="schoolId"
                  label={t(`common.forms.school`)}
                  control={control}
                  options={optionsSchools}
                  disabled={formLoading}
                />
              </Col>
              <Col xs={12}>
                <OutlinedSelect
                  name="campusId"
                  label={t(`common.forms.campus`)}
                  control={control}
                  options={optionsCampuses}
                  disabled={formLoading}
                />
              </Col>
            </Row>

            <div
              className="d-flex flex-wrap justify-content-between mt-5"
              style={{ gap: '1rem' }}
            >
              <Button
                text={t(`common.actions.cancel`)}
                outlined
                style={{ width: isMobile ? '100%' : 197 }}
                onClick={() => setOpenedModal(ModalType.NONE)}
                disabled={formLoading}
              />
              <Button
                type="submit"
                text={t(`common.actions.download`)}
                style={{ width: isMobile ? '100%' : 197 }}
                disabled={formLoading}
              />
            </div>
          </Form>
        </Modal>
      )}

      <div className="d-flex flex-wrap align-items-center " style={{ gap: 12 }}>
        <Button
          text={t(`common.actions.download`)}
          icon="download"
          size="sm"
          outlined
          style={{ width: isMobile ? '90%' : 123 }}
          disabled={!isAuthorizedTo([PermissionName.REPORTS])}
          onClick={() => setOpenedModal(ModalType.FILTER_SELECTOR)}
        />
      </div>
    </>
  );
}
